/**
 * MoneyAmount — FM1 Multi-Currency Amount Display Component
 *
 * Renders a monetary amount with the canonical currency symbol derived
 * from the ISO currency code. Never hardcodes ₹.
 *
 * Rules (FM0-D1 §FIN-INV-007 + §FIN-INV-006):
 *   - Currency symbol is always read from the `currency` prop (ISO code)
 *   - INR → ₹, AED → AED, USD → $, GBP → £, EUR → €
 *   - Missing / null amounts render as "—" (not "0" — truthful per FIN-INV-006)
 *   - Negative amounts are highlighted in red
 *
 * Usage:
 *   <MoneyAmount amount="40000.0000" currency="INR" />
 *   <MoneyAmount amount={summary.outstanding_balance} currency={summary.currency} size="lg" />
 */
import React from "react";

const SYMBOLS = {
  INR: "₹",
  AED: "AED",
  USD: "$",
  GBP: "£",
  EUR: "€",
  SGD: "S$",
  AUD: "A$",
};

const LOCALES = {
  INR: "en-IN",
  AED: "ar-AE",
  USD: "en-US",
  GBP: "en-GB",
  EUR: "de-DE",
};

function formatAmount(amount, currency) {
  if (amount === null || amount === undefined || amount === "") return null;
  const num = typeof amount === "string" ? parseFloat(amount) : Number(amount);
  if (isNaN(num)) return null;
  const locale = LOCALES[currency?.toUpperCase()] || "en-IN";
  return num.toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

const SIZE_CLASSES = {
  xs: "text-xs",
  sm: "text-sm",
  base: "text-base",
  lg: "text-lg font-semibold",
  xl: "text-xl font-bold",
  "2xl": "text-2xl font-bold",
};

export function MoneyAmount({
  amount,
  currency = "INR",
  size = "base",
  className = "",
  "data-testid": testId,
}) {
  const formatted = formatAmount(amount, currency);
  const symbol = SYMBOLS[currency?.toUpperCase()] || currency;
  const isNegative = formatted !== null && parseFloat(amount) < 0;

  const sizeClass = SIZE_CLASSES[size] || SIZE_CLASSES.base;
  const colorClass = isNegative
    ? "text-red-400"
    : "text-slate-100";

  if (formatted === null) {
    return (
      <span
        data-testid={testId}
        className={`${sizeClass} text-slate-500 ${className}`}
        title="Amount not available"
      >
        —
      </span>
    );
  }

  return (
    <span
      data-testid={testId}
      className={`${sizeClass} ${colorClass} tabular-nums ${className}`}
    >
      <span className="text-slate-400 mr-0.5">{symbol}</span>
      {formatted}
    </span>
  );
}

/**
 * MoneyBadge — compact colored variant for balance/outstanding display
 */
export function MoneyBadge({ amount, currency = "INR", variant = "neutral" }) {
  const formatted = formatAmount(amount, currency);
  const symbol = SYMBOLS[currency?.toUpperCase()] || currency;

  const variants = {
    neutral: "bg-slate-800 text-slate-200",
    outstanding: "bg-red-950/50 text-red-300 border border-red-900/40",
    paid: "bg-emerald-950/50 text-emerald-300 border border-emerald-900/40",
    unapplied: "bg-blue-950/50 text-blue-300 border border-blue-900/40",
  };

  const cls = variants[variant] || variants.neutral;

  if (formatted === null) {
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-mono ${cls}`}>—</span>
    );
  }

  return (
    <span className={`px-2 py-0.5 rounded text-xs font-mono tabular-nums ${cls}`}>
      {symbol}{formatted}
    </span>
  );
}
